import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { UserSessionSelector } from '../../../store/UserSession';
import './HelpPanel.css';
import { helpSections, HELP_DOCUMENTATION_PDF, HELP_VIDEO_LIBRARY } from './helpData';
import {
    QuestionIcon,
    SearchIcon,
    ExpandIcon,
    ShrinkIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ScrollDownIcon,
    DownloadIcon,
    VideoLibraryIcon,
} from '../../../icons/HelpPanelIcon';

const VIEW_HOME = 'home';
const VIEW_SECTION = 'section';
const VIEW_ARTICLE = 'article';
const VIEW_VIDEOS = 'videos';

export default function UseCasePanel() {
    const { t } = useTranslation();
    const { user } = useSelector(UserSessionSelector);
    const userRole = user?.role || '';

    const tr = (text) => (text ? t(text) : text);

    const visibleSections = helpSections.filter(s =>
        s.visibleFor.includes(userRole)
    );

    const [open, setOpen] = useState(false);
    const [view, setView] = useState(VIEW_HOME);
    const [activeSection, setActiveSection] = useState(null);
    const [activeArticle, setActiveArticle] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [articleLarge, setArticleLarge] = useState(true);
    const [tocOpen, setTocOpen] = useState(false);
    const [showScrollBtn, setShowScrollBtn] = useState(false);
    const [playingVideo, setPlayingVideo] = useState(null);
    const [showNextVideoChoice, setShowNextVideoChoice] = useState(false);

    const panelRef = useRef(null);
    const searchRef = useRef(null);
    const bodyRef = useRef(null);
    const videoPlayerRef = useRef(null);

    const playingVideoIndex = playingVideo
        ? HELP_VIDEO_LIBRARY.findIndex((v) => v.id === playingVideo.id)
        : -1;
    const nextVideoIndex =
        playingVideoIndex >= 0
            ? (playingVideoIndex + 1) % HELP_VIDEO_LIBRARY.length
            : -1;
    const nextVideo =
        nextVideoIndex >= 0 ? HELP_VIDEO_LIBRARY[nextVideoIndex] : null;

    const closePlayingVideo = useCallback(() => {
        setShowNextVideoChoice(false);
        setPlayingVideo(null);
    }, []);

    const openPlayingVideo = useCallback((video) => {
        setShowNextVideoChoice(false);
        setPlayingVideo(video);
    }, []);

    const playNextVideo = useCallback(() => {
        if (!nextVideo) return;
        setShowNextVideoChoice(false);
        setPlayingVideo(nextVideo);
    }, [nextVideo]);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (
                panelRef.current &&
                !panelRef.current.contains(e.target) &&
                !e.target.closest('.hp-trigger-btn')
            ) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    useEffect(() => {
        if (open && view === VIEW_HOME)
            setTimeout(() => searchRef.current?.focus(), 120);
    }, [open, view]);

    const handleBodyScroll = useCallback(() => {
        const el = bodyRef.current;
        if (!el) return;
        setShowScrollBtn(el.scrollTop + el.clientHeight < el.scrollHeight - 40);
    }, []);

    useEffect(() => {
        const el = bodyRef.current;
        if (!el) return;
        el.addEventListener('scroll', handleBodyScroll);
        handleBodyScroll();
        return () => el.removeEventListener('scroll', handleBodyScroll);
    }, [view, handleBodyScroll]);

    useEffect(() => {
        if (!playingVideo) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') closePlayingVideo();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [playingVideo, closePlayingVideo]);

    useEffect(() => {
        if (!playingVideo || showNextVideoChoice || !videoPlayerRef.current) return;
        const video = videoPlayerRef.current;
        video.load();
        const playPromise = video.play();
        if (playPromise?.catch) {
            playPromise.catch(() => {});
        }
    }, [playingVideo, showNextVideoChoice]);

    const togglePanel = () => {
        setOpen(prev => {
            if (!prev) {
                setView(VIEW_HOME);
                setSearchQuery('');
                setActiveSection(null);
                setActiveArticle(null);
                setArticleLarge(true);
                setTocOpen(false);
                setPlayingVideo(null);
                setShowNextVideoChoice(false);
            }
            return !prev;
        });
    };

    const goToSection = (section) => {
        setActiveSection(section);
        setView(VIEW_SECTION);
        setSearchQuery('');
    };

    const goToArticle = useCallback((article, section) => {
        if (section) setActiveSection(section);
        setActiveArticle(article);
        setView(VIEW_ARTICLE);
        setArticleLarge(true);
        setTocOpen(false);
        setTimeout(() => {
            if (bodyRef.current) {
                bodyRef.current.scrollTop = 0;
                handleBodyScroll();
            }
        }, 50);
    }, [handleBodyScroll]);

    const goBack = () => {
        if (view === VIEW_ARTICLE) {
            setView(VIEW_SECTION);
            setActiveArticle(null);
        } else if (view === VIEW_SECTION || view === VIEW_VIDEOS) {
            setView(VIEW_HOME);
            setActiveSection(null);
            closePlayingVideo();
        }
    };

    const goToVideoLibrary = () => {
        setView(VIEW_VIDEOS);
        setSearchQuery('');
        setActiveSection(null);
        setActiveArticle(null);
        setArticleLarge(true);
        closePlayingVideo();
    };

    const scrollToBottom = () => {
        if (bodyRef.current)
            bodyRef.current.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' });
    };

    const handleDocumentationDownload = () => {
        const link = document.createElement('a');
        link.href = HELP_DOCUMENTATION_PDF.url;
        link.download = HELP_DOCUMENTATION_PDF.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const scrollToStep = (idx) => {
        const el = bodyRef.current?.querySelector(`[data-step="${idx}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTocOpen(false);
    };

    const filteredResults = useCallback(() => {
        const q = searchQuery.toLowerCase().trim();
        if (!q) return null;
        const results = [];
        visibleSections.forEach(section => {
            section.articles.forEach(article => {
                if (
                    article.label.toLowerCase().includes(q) ||
                    tr(article.label).toLowerCase().includes(q) ||
                    section.title.toLowerCase().includes(q) ||
                    tr(section.title).toLowerCase().includes(q)
                ) results.push({ section, article });
            });
        });
        return results;
    }, [searchQuery, visibleSections, t]);

    const headerTitle = () => {
        if (view === VIEW_ARTICLE && activeArticle) return tr(activeArticle.label);
        if (view === VIEW_SECTION && activeSection) return tr(activeSection.title);
        if (view === VIEW_VIDEOS) return tr('Bibliothèque Vidéos tuto');
        return tr(`Centre d'aide`);
    };

    const panelClass = [
        'hp-panel',
        view === VIEW_ARTICLE || view === VIEW_SECTION || view === VIEW_VIDEOS
            ? articleLarge ? 'hp-panel--article-large' : 'hp-panel--article-small'
            : '',
    ].filter(Boolean).join(' ');

    // Recherche d'article par référence textuelle (améliorée)
    const findArticleByRefText = (refText) => {
        const cleanRef = refText.replace(/[«»"']/g, '').trim().toLowerCase();
        // Correspondance exacte sur le label
        for (const section of visibleSections) {
            for (const article of section.articles) {
                const labelClean = article.label.toLowerCase();
                if (labelClean === cleanRef || labelClean.includes(cleanRef) || cleanRef.includes(labelClean)) {
                    return { section, article };
                }
            }
        }
        // Fallback sur mots-clés significatifs
        const significant = (str) => str.toLowerCase().split(/\s+/).filter(w => w.length > 3);
        const refWords = new Set(significant(cleanRef));
        let bestMatch = null;
        let bestScore = 0;
        visibleSections.forEach(section => {
            section.articles.forEach(article => {
                const labelWords = new Set(significant(article.label));
                let overlap = 0;
                refWords.forEach(w => { if (labelWords.has(w)) overlap++; });
                const score = overlap / Math.max(refWords.size, labelWords.size);
                if (score > bestScore) { bestScore = score; bestMatch = { section, article }; }
            });
        });
        return bestScore >= 0.35 ? bestMatch : null;
    };

    const formatBoldText = (text) => {
        if (typeof text !== 'string') return text;
        const parts = text.split(/(\*\*.*?\*\*)/g);
        return parts.map((part, idx) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return (
                    <strong key={idx} className="hp-desc-markdown-strong">
                        {part.slice(2, -2)}
                    </strong>
                );
            }
            return part;
        });
    };

    const renderImages = (src, size) => {
        if (!src) return null;
        let extraClass = '';
        if (size === 'small') extraClass = ' hp-article-step-img--small';
        else if (size === 'xsmall') extraClass = ' hp-article-step-img--xsmall';
        return (
            <div className={`hp-article-step-img${extraClass}`}>
                <img src={src} alt="" />
            </div>
        );
    };

    const renderCalloutLine = (line, key) => {
        const quotedMatch = line.match(/[""«\u00ab\u00bb]([^""»\u00ab\u00bb]+)[""»\u00ab\u00bb]/);
        if (quotedMatch) {
            const refText = quotedMatch[1].replace(/\u00a0/g, ' ').trim();
            const found = findArticleByRefText(refText);
            if (found) {
                const before = line.slice(0, line.search(/[""«\u00ab]/));
                const after = line.slice(line.indexOf(quotedMatch[0]) + quotedMatch[0].length);
                return (
                    <p key={key} className="hp-desc-callout">
                        {before}
                        <button
                            className="hp-crossref-link"
                            onClick={() => goToArticle(found.article, found.section)}
                        >
                            {quotedMatch[0]}
                        </button>
                        {after}
                    </p>
                );
            }
        }
        return <p key={key} className="hp-desc-callout">{line}</p>;
    };

    const renderBulletContent = (raw) => {
        const dashMatch = raw.match(/^(.+?)\s+—\s+(.+)$/);
        const colonMatch = raw.match(/^(.+?)\s*:\s(.+)$/);
        if (dashMatch) return <><strong>{dashMatch[1]}</strong> — {dashMatch[2]}</>;
        if (colonMatch) return <><strong>{colonMatch[1]}</strong> : {colonMatch[2]}</>;
        return <>{raw}</>;
    };

    const renderDescription = (text) => {
        if (!text || typeof text !== 'string') return null;
        const blocks = text.split('\n\n');
        return blocks.map((block, bi) => {
            if (/^---+$/s.test(block.trim())) {
                return <hr key={`b${bi}`} className="hp-desc-rule" />;
            }
            const lines = block.split('\n').filter(l => l !== '');
            if (!lines.length) return null;

            // Liste numérotée
            const numberedListRx = /^\d+\.\s/;
            if (lines.every(l => numberedListRx.test(l.trim()))) {
                return (
                    <ol key={`b${bi}`} className="hp-desc-list hp-desc-list--numbered">
                        {lines.map((line, li) => (
                            <li key={li}>{formatBoldText(line.replace(/^\d+\.\s*/, ''))}</li>
                        ))}
                    </ol>
                );
            }

            // Liste à puces standard
            if (lines.every(l => l.trim().startsWith('•'))) {
                return (
                    <ul key={`b${bi}`} className="hp-desc-list">
                        {lines.map((line, li) => (
                            <li key={li}>{renderBulletContent(line.replace(/^•\s*/, ''))}</li>
                        ))}
                    </ul>
                );
            }

            // Liste à flèches 👉
            if (lines.every(l => l.trim().startsWith('👉'))) {
                if (lines.length === 1) return renderCalloutLine(lines[0], `b${bi}`);
                return (
                    <ul key={`b${bi}`} className="hp-desc-list hp-desc-list--arrows">
                        {lines.map((l, li) => <li key={li}>{formatBoldText(l.replace(/^👉\s*/, ''))}</li>)}
                    </ul>
                );
            }

            // Mix : première ligne texte + flèches
            if (lines.length > 1 && lines.slice(1).every(l => l.trim().startsWith('👉'))) {
                return (
                    <div key={`b${bi}`} className="hp-desc-mixed">
                        <p className="hp-desc-para hp-desc-para--tight">{formatBoldText(lines[0])}</p>
                        <ul className="hp-desc-list hp-desc-list--arrows">
                            {lines.slice(1).map((l, li) => <li key={li}>{formatBoldText(l.replace(/^👉\s*/, ''))}</li>)}
                        </ul>
                    </div>
                );
            }

            // Paragraphe simple
            return <p key={`b${bi}`} className="hp-desc-para">{formatBoldText(block)}</p>;
        }).filter(Boolean);
    };

    const renderPermissionCell = (allowed) => (
        <span
            className={`hp-perm-icon${allowed ? ' hp-perm-icon--yes' : ' hp-perm-icon--no'}`}
            aria-label={allowed ? tr('Oui') : tr('Non')}
            role="img"
        >
            {allowed ? '✓' : '✕'}
        </span>
    );

    const renderPermissionsTable = (block, idx) => {
        const headers = (block.headers || [
            'Fonctionnalités / Rôles',
            'Administrateur',
            'Chef de projet',
            'Rédacteur',
        ]).map(tr);
        const rows = block.rows || [];
        return (
            <div key={idx} className="hp-perm-table-wrap">
                <table className="hp-perm-table">
                    <thead>
                        <tr>
                            {headers.map((h, hi) => (
                                <th
                                    key={hi}
                                    scope="col"
                                    className={hi > 0 ? 'hp-perm-table-role-head' : 'hp-perm-table-feature-head'}
                                >
                                    {hi > 0 ? <strong>{h}</strong> : h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, ri) => (
                            <tr key={ri}>
                                <th scope="row">{tr(row.label)}</th>
                                <td>{renderPermissionCell(row.values[0])}</td>
                                <td>{renderPermissionCell(row.values[1])}</td>
                                <td>{renderPermissionCell(row.values[2])}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderCreditsTable = (block, idx) => {
        const headers = (block.headers || []).map(tr);
        const rows = block.rows || [];
        return (
            <div key={idx} className="hp-perm-table-wrap">
                <table className="hp-perm-table">
                    <thead>
                        <tr>
                            {headers.map((h, hi) => (
                                <th
                                    key={hi}
                                    scope="col"
                                    className={hi > 0 ? 'hp-perm-table-role-head' : 'hp-perm-table-feature-head'}
                                >
                                    {hi > 0 ? <strong>{h}</strong> : h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, ri) => (
                            <tr key={ri}>
                                <th scope="row">{tr(row.label)}</th>
                                <td>{tr(row.value)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderContentBlock = (block, idx) => {
        if (block.type === 'text') {
            return <div key={idx} className="hp-article-step-body">{renderDescription(tr(block.content))}</div>;
        } else if (block.type === 'image') {
            let extraClass = '';
            if (block.size === 'small') extraClass = ' hp-article-step-img--small';
            else if (block.size === 'xsmall') extraClass = ' hp-article-step-img--xsmall';
            return (
                <div key={idx} className={`hp-article-step-img${extraClass}`}>
                    <img src={block.src} alt="" />
                </div>
            );
        } else if (block.type === 'permissionsTable') {
            return renderPermissionsTable(block, idx);
        } else if (block.type === 'creditsTable') {
            return renderCreditsTable(block, idx);
        }
        return null;
    };

    const renderHome = () => {
        const results = filteredResults();
        if (results !== null) {
            return (
                <div className="hp-body" ref={bodyRef}>
                    {results.length === 0 ? (
                        <div className="hp-empty">
                            <div className="hp-empty-icon">🔍</div>
                            {tr('Aucun résultat pour')} «&nbsp;{searchQuery}&nbsp;»
                        </div>
                    ) : (
                        <>
                            <div style={{ padding: '10px 16px 4px' }}>
                                <span style={{ fontSize: 11, color: '#bbb' }}>
                                    {results.length} {results.length > 1 ? tr('résultats') : tr('résultat')}
                                </span>
                            </div>
                            {results.map(({ section, article }) => (
                                <div
                                    key={article.id}
                                    className="hp-article-item"
                                    onClick={() => goToArticle(article, section)}
                                    role="button" tabIndex={0}
                                    onKeyDown={e => e.key === 'Enter' && goToArticle(article, section)}
                                >
                                    <div className="hp-article-number"
                                        style={{ background: section.bg, color: section.color }}>
                                        {section.icon}
                                    </div>
                                    <span className="hp-article-label">{tr(article.label)}</span>
                                    <span className="hp-article-arrow">›</span>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            );
        }

        return (
            <div className="hp-body" ref={bodyRef}>
                <div className="hp-collections-intro">
                    <div className="hp-collections-intro-title">
                        {visibleSections.length} {visibleSections.length > 1 ? tr('collections') : tr('collection')}
                    </div>
                </div>
                {visibleSections.map(section => (
                    <div
                        key={section.id}
                        className="hp-collection-card"
                        onClick={() => goToSection(section)}
                        role="button" tabIndex={0}
                        onKeyDown={e => e.key === 'Enter' && goToSection(section)}
                    >
                        <div className="hp-collection-icon" style={{ background: section.bg }}>
                            {section.icon}
                        </div>
                        <div className="hp-collection-info">
                            <div className="hp-collection-name">{tr(section.title)}</div>
                            <div className="hp-collection-desc">{tr(section.description)}</div>
                            <span className="hp-collection-count-badge">
                                {section.articles.length} {section.articles.length > 1 ? tr('tutoriels') : tr('tutoriel')}
                            </span>
                        </div>
                        <span className="hp-collection-arrow">›</span>
                    </div>
                ))}
            </div>
        );
    };

    const renderSection = () => {
        if (!activeSection) return null;
        return (
            <div className="hp-body" ref={bodyRef}>
                <div className="hp-section-header-banner">
                    <div className="hp-section-banner-row">
                        <div className="hp-section-banner-icon" style={{ background: activeSection.bg }}>
                            {activeSection.icon}
                        </div>
                        <div className="hp-section-banner-title" style={{ color: activeSection.color }}>
                            {tr(activeSection.title)}
                        </div>
                    </div>
                    <div className="hp-section-banner-desc">{tr(activeSection.description)}</div>
                </div>
                <div className="hp-section-article-count">
                    {activeSection.articles.length} {activeSection.articles.length > 1 ? tr('tutoriels') : tr('tutoriel')}
                </div>
                {activeSection.articles.map((article, idx) => (
                    <div
                        key={article.id}
                        className="hp-article-item"
                        onClick={() => goToArticle(article)}
                        role="button" tabIndex={0}
                        onKeyDown={e => e.key === 'Enter' && goToArticle(article)}
                    >
                        <div className="hp-article-number"
                            style={{ background: activeSection.bg, color: activeSection.color }}>
                            {idx + 1}
                        </div>
                        <span className="hp-article-label">{tr(article.label)}</span>
                        <span className="hp-article-arrow">›</span>
                    </div>
                ))}
            </div>
        );
    };

    const renderArticle = () => {
        if (!activeArticle || !activeSection) return null;
        return (
            <div className="hp-body" ref={bodyRef} style={{ position: 'relative' }}>
                <div className="hp-article-content">
                    <div className="hp-article-meta">
                        <div className="hp-article-avatar">{activeSection.icon}</div>
                        <div className="hp-article-meta-info">
                            <span className="hp-article-author">{tr('Équipe Typix Néo')}</span>
                            <span className="hp-article-date">{tr('Mis à jour récemment')}</span>
                        </div>
                    </div>

                    {!activeArticle.hideStepNumbers && (
                    <div className="hp-toc">
                        <div
                            className="hp-toc-header"
                            onClick={() => setTocOpen(prev => !prev)}
                            role="button" tabIndex={0}
                            onKeyDown={e => e.key === 'Enter' && setTocOpen(p => !p)}
                        >
                            <span className="hp-toc-header-label">{tr('Table des matières')}</span>
                            {tocOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </div>
                        {tocOpen && (
                            <div className="hp-toc-list">
                                {activeArticle.steps.map((step, idx) => (
                                    <div
                                        key={idx}
                                        className="hp-toc-item"
                                        onClick={() => scrollToStep(idx)}
                                        role="button" tabIndex={0}
                                        onKeyDown={e => e.key === 'Enter' && scrollToStep(idx)}
                                    >
                                        <div className="hp-toc-dot" style={{ background: activeSection.color }} />
                                        <span className="hp-toc-label">{tr(step.title)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    )}

                    {activeArticle.description && (
                        <div className="hp-article-intro">
                            {renderDescription(tr(activeArticle.description))}
                            {renderImages(activeArticle.screenshot, activeArticle.imageSize)}
                        </div>
                    )}

                    <div className="hp-article-steps">
                        {activeArticle.steps.map((step, idx) => (
                            <div
                                key={idx}
                                className={[
                                    'hp-article-step',
                                    activeArticle.hideStepNumbers ? 'hp-article-step--flat' : '',
                                ].filter(Boolean).join(' ')}
                                data-step={idx}
                            >
                                {!activeArticle.hideStepNumbers && (
                                <div className="hp-article-step-heading">
                                    <div className="hp-article-step-badge"
                                        style={{ background: activeSection.color }}>
                                        {idx + 1}
                                    </div>
                                    <h2 className="hp-article-step-title">{tr(step.title)}</h2>
                                </div>
                                )}

                                {step.contentBlocks ? (
                                    // contentBlocks path: handles all mixed image/text layouts
                                    step.contentBlocks.map((block, bi) => renderContentBlock(block, bi))
                                ) : (
                                    <>
                                        {step.screenshotFirst ? (
                                            <>
                                                {renderImages(step.screenshot, step.imageSize)}
                                                <div className="hp-article-step-body">
                                                    {renderDescription(tr(step.description))}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="hp-article-step-body">
                                                    {renderDescription(tr(step.description))}
                                                </div>
                                                {renderImages(step.screenshot, step.imageSize)}
                                                {step.descriptionAfterImage && (
                                                    <div className="hp-article-step-body hp-article-step-body--after-image">
                                                        {renderDescription(tr(step.descriptionAfterImage))}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {step.screenshots && (
                                            <div className="hp-article-step-img--multi-row">
                                                {step.screenshots.map((src, i) => (
                                                    <div
                                                        key={i}
                                                        className={`hp-article-step-img${step.imageSize === 'small' ? ' hp-article-step-img--small' : step.imageSize === 'xsmall' ? ' hp-article-step-img--xsmall' : ''}`}
                                                    >
                                                        <img src={src} alt="" />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <button
                    className={`hp-scroll-btn${showScrollBtn ? '' : ' hidden'}`}
                    onClick={scrollToBottom}
                    aria-label={tr('Défiler vers le bas')}
                >
                    <ScrollDownIcon />
                </button>
            </div>
        );
    };

    const renderVideos = () => (
        <div className="hp-body" ref={bodyRef}>
            <div className="hp-collections-intro">
                <div className="hp-collections-intro-title">
                    {HELP_VIDEO_LIBRARY.length}{' '}
                    {HELP_VIDEO_LIBRARY.length > 1 ? tr('vidéos') : tr('vidéo')}
                </div>
            </div>
            <div className="hp-video-list">
                {HELP_VIDEO_LIBRARY.map((video) => (
                    <button
                        key={video.id}
                        type="button"
                        className="hp-video-item"
                        onClick={() => openPlayingVideo(video)}
                    >
                        <div className="hp-video-thumb">
                            <video
                                src={video.src}
                                muted
                                preload="metadata"
                                onLoadedMetadata={(e) => {
                                    try {
                                        e.currentTarget.currentTime = 0.5;
                                    } catch {
                                        // ignore
                                    }
                                }}
                            />
                            <span className="hp-video-thumb-play" aria-hidden="true">▶</span>
                        </div>
                        <div className="hp-video-info">
                            <span className="hp-video-title">{tr(video.titleKey)}</span>
                        </div>
                        <span className="hp-video-arrow">›</span>
                    </button>
                ))}
            </div>
        </div>
    );

    return (
        <>
            <button
                className="hp-trigger-btn"
                onClick={togglePanel}
                title={tr("Centre d'aide")}
                aria-label={tr("Ouvrir le centre d'aide")}
            >
                <QuestionIcon />
            </button>

            {open && (
                <div className={panelClass} ref={panelRef} role="dialog"
                    aria-label={tr("Centre d'aide")} style={{ position: 'fixed' }}>

                    <div className="hp-header">
                        <div className="hp-header-left">
                            {view !== VIEW_HOME && (
                                <button className="hp-back-btn" onClick={goBack} aria-label={tr('Retour')}>
                                    ‹
                                </button>
                            )}
                            <span className="hp-header-title" title={headerTitle()}>
                                {headerTitle()}
                            </span>
                        </div>
                        <div className="hp-header-actions">
                            {(view === VIEW_ARTICLE || view === VIEW_SECTION || view === VIEW_VIDEOS) && (
                                <button
                                    className="hp-icon-btn"
                                    onClick={() => setArticleLarge(prev => !prev)}
                                    aria-label={articleLarge ? tr('Réduire') : tr('Agrandir')}
                                    title={articleLarge ? tr('Réduire') : tr('Agrandir')}
                                >
                                    {articleLarge ? <ShrinkIcon /> : <ExpandIcon />}
                                </button>
                            )}
                            <button className="hp-icon-btn" onClick={() => setOpen(false)} aria-label={tr('Fermer')}>
                                ✕
                            </button>
                        </div>
                    </div>

                    {view === VIEW_HOME && (
                        <div className="hp-search">
                            <div className="hp-search-inner">
                                <SearchIcon />
                                <input
                                    ref={searchRef}
                                    type="text"
                                    placeholder={tr('Rechercher un tutoriel…')}
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    aria-label={tr('Rechercher un tutoriel')}
                                />
                            </div>
                        </div>
                    )}

                    {view === VIEW_HOME && renderHome()}
                    {view === VIEW_SECTION && renderSection()}
                    {view === VIEW_ARTICLE && renderArticle()}
                    {view === VIEW_VIDEOS && renderVideos()}

                    <div className="hp-footer">
                        <div className="hp-footer-actions">
                            <button
                                type="button"
                                className="hp-download-btn"
                                onClick={goToVideoLibrary}
                            >
                                <VideoLibraryIcon />
                                <span>{tr('Bibliothèque Vidéos tuto')}</span>
                            </button>
                            <button
                                type="button"
                                className="hp-download-btn"
                                onClick={handleDocumentationDownload}
                            >
                                <DownloadIcon />
                                <span>{tr('Télécharger la documentation (PDF)')}</span>
                            </button>
                        </div>
                        <a href="mailto:support@typix.fr">{tr('Contacter le support →')}</a>
                    </div>
                </div>
            )}

            {playingVideo &&
                createPortal(
                    <div
                        className="hp-video-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-label={tr(playingVideo.titleKey)}
                        onClick={closePlayingVideo}
                    >
                        <div
                            className="hp-video-modal__content"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                type="button"
                                className="hp-video-modal__close"
                                aria-label={tr('Fermer')}
                                onClick={closePlayingVideo}
                            >
                                ✕
                            </button>
                            <video
                                ref={videoPlayerRef}
                                key={playingVideo.src}
                                className="hp-video-modal__player"
                                src={playingVideo.src}
                                controls
                                autoPlay
                                playsInline
                                onEnded={() => setShowNextVideoChoice(true)}
                            >
                                <source src={playingVideo.src} type="video/mp4" />
                            </video>

                            {showNextVideoChoice && nextVideo && (
                                <div className="hp-video-modal__choice">
                                    <p className="hp-video-modal__choice-title">
                                        {tr('Vidéo terminée')}
                                    </p>
                                    <p className="hp-video-modal__choice-text">
                                        {tr('Souhaitez-vous regarder la vidéo suivante ?')}
                                    </p>
                                    <p className="hp-video-modal__choice-next">
                                        {tr(nextVideo.titleKey)}
                                    </p>
                                    <div className="hp-video-modal__choice-actions">
                                        <button
                                            type="button"
                                            className="hp-video-modal__choice-btn hp-video-modal__choice-btn--secondary"
                                            onClick={closePlayingVideo}
                                        >
                                            {tr('Fermer')}
                                        </button>
                                        <button
                                            type="button"
                                            className="hp-video-modal__choice-btn hp-video-modal__choice-btn--primary"
                                            onClick={playNextVideo}
                                        >
                                            {tr('Vidéo suivante')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
}